/**
 * Shared markdown → HTML rendering — the SINGLE grammar used by every surface
 * (PK-QA-35). Pure, DOM-free string functions (regex + string ops only, zero
 * browser/React dependency) so BOTH `@eer/web` (on-screen View + `.doc` Word
 * export) AND `@eer/api` (outbound email body wrapping) render markdown through
 * ONE implementation — no second, divergable parser.
 *
 * Two layers:
 *   - inline: `renderMarkdownInline(line)` → typed `InlineSegment[]` (no HTML),
 *     driven by the `INLINE_RULES` table. Handles `**bold**`, `__underline__`,
 *     `*italic*`/`_italic_`, `[text](href)` markdown links + bare-URL autolinks.
 *     The link scheme allow-list (`http(s):`/`mailto:` only, PK-QA-23) is a
 *     security fix and MUST be preserved.
 *   - block: `renderFreeMarkdown(raw)` → escaped HTML (headings / bullet lists /
 *     paragraphs), built on top of `inlineMd` (which maps each segment to an
 *     `esc`-escaped tag). Every segment's text/href is `esc`-escaped, so untrusted
 *     LLM/user text can never inject raw HTML.
 *
 * Relocated here from `packages/web/src/document/{docGenerationModel,docExport}.ts`
 * (PK-QA-23/PK-QA-24 origin) without behaviour change — the web View/Export and
 * the api email path now import the same functions.
 */
import { isBlankListItem } from './richText.js';
/**
 * Parse rules, in tie-break order (earliest match index wins; equal index → first
 * rule here). Link rules come FIRST so a `[text](url)` markdown link is taken as a
 * whole before its inner URL could be autolinked, and `**`/`__` (two-char) precede
 * `*`/`_` (one-char). PK-QA-23 adds the two link rules.
 */
const INLINE_RULES = [
    // Markdown link: `[text](href)`. text=group1, href=group2 (href may not contain ')').
    // href comes verbatim from LLM output, which is not trusted (same reason this
    // file never uses dangerouslySetInnerHTML) — restrict to http(s)/mailto so a
    // generated `javascript:`/`data:` URI can never reach a real <a href> and
    // execute on click. A disallowed scheme falls back to plain literal text.
    {
        re: /\[([^\]]+)\]\(([^)]+)\)/,
        build: (m) => {
            const text = m[1];
            const href = m[2];
            if (!/^(https?:|mailto:)/i.test(href)) {
                return { segment: { kind: 'text', text: m[0] }, consumed: m[0].length };
            }
            return { segment: { kind: 'link', text, href }, consumed: m[0].length };
        },
    },
    // Bare-URL autolink: `https?://…` up to whitespace, then STRIP trailing
    // punctuation (`.`/`,`/`;`/`:`/`!`/`?`/`)`/`]`/`}`/quotes) so a sentence-ending
    // dot or a wrapping paren is NOT swallowed into the href — it stays as text.
    {
        re: /https?:\/\/\S+/,
        build: (m) => {
            const raw = m[0];
            const trimmed = raw.replace(/[.,;:!?)\]}'"]+$/, '');
            const url = trimmed.length > 0 ? trimmed : raw;
            return { segment: { kind: 'link', text: url, href: url }, consumed: url.length };
        },
    },
    {
        re: /\*\*([^*]+)\*\*/,
        build: (m) => ({ segment: { kind: 'bold', text: m[1] }, consumed: m[0].length }),
    },
    {
        re: /__([^_]+)__/,
        build: (m) => ({ segment: { kind: 'underline', text: m[1] }, consumed: m[0].length }),
    },
    {
        re: /\*([^*]+)\*/,
        build: (m) => ({ segment: { kind: 'italic', text: m[1] }, consumed: m[0].length }),
    },
    {
        re: /_([^_]+)_/,
        build: (m) => ({ segment: { kind: 'italic', text: m[1] }, consumed: m[0].length }),
    },
];
/**
 * Parse a single line of inline markdown into typed segments (no HTML). Handles
 * `[text](href)` markdown links + bare-URL autolinks (PK-QA-23), `**bold**`,
 * `__underline__`, `*italic*` / `_italic_`. Greedy left-to-right; unmatched
 * markers stay as literal text. The component maps each segment to a
 * `<a>` / `<strong>` / `<em>` / `<u>` / text node — so there is never raw HTML
 * injection (no `dangerouslySetInnerHTML`; the href/text are React-set).
 */
export function renderMarkdownInline(line) {
    const segments = [];
    let rest = line;
    let guard = 0;
    while (rest.length > 0 && guard++ < 1000) {
        let bestIdx = -1;
        let bestRule = null;
        let bestMatch = null;
        for (const rule of INLINE_RULES) {
            const m = rest.match(rule.re);
            if (m && m.index !== undefined && (bestIdx === -1 || m.index < bestIdx)) {
                bestIdx = m.index;
                bestRule = rule;
                bestMatch = m;
            }
        }
        if (!bestRule || !bestMatch || bestIdx === -1) {
            segments.push({ kind: 'text', text: rest });
            break;
        }
        if (bestIdx > 0)
            segments.push({ kind: 'text', text: rest.slice(0, bestIdx) });
        const { segment, consumed } = bestRule.build(bestMatch);
        segments.push(segment);
        rest = rest.slice(bestIdx + consumed);
    }
    return segments;
}
// ---------------------------------------------------------------------------
// Block markdown → escaped HTML (esc / inlineMd / renderFreeMarkdown)
// ---------------------------------------------------------------------------
/** HTML-escape a value for safe insertion into the exported/rendered markup. */
export function esc(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/**
 * Inline markdown → ESCAPED HTML. Reuses the SINGLE grammar in
 * `renderMarkdownInline` (the exact parser the on-screen viewer uses — no second,
 * divergable implementation, per the ADR) and maps its safe typed segments to
 * tags: `**bold**`→`<b>`, `__underline__`→`<u>`, `*italic*`/`_italic_`→`<i>`,
 * `[t](href)`/bare-URL→`<a>`. Every segment's text/href is `esc`-escaped, so LLM
 * output can never inject raw HTML.
 */
export function inlineMd(line) {
    return renderMarkdownInline(line)
        .map((s) => {
        switch (s.kind) {
            case 'bold':
                return `<b>${esc(s.text)}</b>`;
            case 'italic':
                return `<i>${esc(s.text)}</i>`;
            case 'underline':
                return `<u>${esc(s.text)}</u>`;
            case 'link':
                return `<a href="${esc(s.href)}">${esc(s.text)}</a>`;
            default:
                return esc(s.text);
        }
    })
        .join('');
}
/**
 * Render a FREE-MARKDOWN string (the kickoff_sheet body, the sourcing-sheet
 * markdown-blob fallback, or an outbound email body) into HTML with block +
 * inline transforms (PK-QA-24/PK-QA-35). Block transforms:
 *   - `#`/`##`/`### ` headings → `<h1>`/`<h2>`/`<h3>`;
 *   - `- ` / `* ` bullet lines → one `<ul><li>…</li></ul>` run;
 *   - blank-line-separated paragraphs → `<p>` (inner newlines → `<br>`).
 * Inline emphasis + links are applied inside every block via {@link inlineMd},
 * which escapes each segment — no raw HTML from the model reaches the output.
 * Scoped to free-markdown-string content ONLY; structured serializers
 * (jd_doc / interview_questions / structured sourcing_sheet) are untouched.
 */
export function renderFreeMarkdown(raw) {
    const text = String(raw ?? '').trim();
    if (!text)
        return '';
    const out = [];
    let para = [];
    let bullets = [];
    const flushPara = () => {
        if (para.length) {
            out.push(`<p>${para.map(inlineMd).join('<br>')}</p>`);
            para = [];
        }
    };
    const flushBullets = () => {
        if (bullets.length) {
            out.push(`<ul>${bullets.map((b) => `<li>${inlineMd(b)}</li>`).join('')}</ul>`);
            bullets = [];
        }
    };
    for (const rawLine of text.split('\n')) {
        const line = rawLine.replace(/\s+$/, '');
        if (line.trim() === '') {
            flushPara();
            flushBullets();
            continue;
        }
        // `#{1,3} ` heading → h1..h3 (marker stripped, inline markdown applied to the rest).
        const heading = line.match(/^(#{1,3})\s+(.*)$/);
        if (heading) {
            flushPara();
            flushBullets();
            const level = heading[1].length;
            out.push(`<h${level}>${inlineMd(heading[2])}</h${level}>`);
            continue;
        }
        // `- ` / `* ` bullet (marker MUST be followed by a space, so `*italic*` and a
        // `---` rule never register as bullets). Consecutive bullets group into one <ul>.
        const bullet = line.match(/^\s*[-*]\s+(.+)$/);
        if (bullet) {
            flushPara();
            // E03-F08-T06 — `- &nbsp;` / `- •` is a marker with no text: drop the item.
            if (!isBlankListItem(bullet[1]))
                bullets.push(bullet[1]);
            continue;
        }
        // Ordinary prose line — ends any open bullet run, then accumulates a paragraph.
        flushBullets();
        para.push(line);
    }
    flushPara();
    flushBullets();
    return out.join('');
}
//# sourceMappingURL=markdown.js.map